// Contract — read-only status view (PivotPhase-4 revised). Creator-facing.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/brand/AppShell";
import { apiClient } from "../auth/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_UI = {
  not_assigned: { label: "Awaiting Assignment", cls: "border-brand-text/20 text-brand-text/60",
                  body: "Awaiting contract assignment from leadership. We'll email you the moment your contract is ready." },
  sent:         { label: "Sent · Awaiting Signature", cls: "border-brand-gold/40 text-brand-gold",
                  body: "Your contract has been emailed to you. Please review, sign, and return the signed copy to your manager." },
  signed:       { label: "Signed · Pending Activation", cls: "border-emerald-400/40 text-emerald-300",
                  body: "We've received your signed copy. Activation will be confirmed by leadership." },
  active:       { label: "Active", cls: "border-emerald-400/40 text-emerald-300",
                  body: "Your management agreement is active. Welcome to the roster." },
  expired:      { label: "Expired", cls: "border-brand-text/30 text-brand-text/60",
                  body: "Your contract term has ended. Reach out to leadership about renewal." },
  terminated:   { label: "Terminated", cls: "border-rose-400/40 text-rose-300",
                  body: "Your contract has been terminated. Reach out to leadership if you have questions." },
};

export default function Contract() {
  const [c, setC] = useState(null);
  useEffect(() => { document.title = "Contract · Omertàbella"; }, []);
  useEffect(() => { apiClient.get("/me/contract").then((r) => setC(r.data)); }, []);
  if (!c) return <AppShell><div className="p-12 text-center text-brand-text/60">Loading…</div></AppShell>;
  const ui = STATUS_UI[c.status] || STATUS_UI.not_assigned;
  return (
    <AppShell>
      <div data-testid="contract-page" className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/portal" className="text-xs uppercase tracking-[0.24em] text-brand-text/50 hover:text-brand-gold">← Portal</Link>
        <p className="mt-6 text-[10px] uppercase tracking-[0.32em] text-brand-gold">Step 02 · Management Agreement</p>
        <h1 className="mt-2 font-display text-4xl text-brand-text sm:text-5xl">Your contract.</h1>

        <div data-testid="contract-status-card" className="mt-8 rounded border border-brand-gold/30 bg-[#1A1A1A] p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-brand-text/50">Status</div>
              <div className="mt-2 inline-flex">
                <span data-testid="contract-status-pill" className={`rounded-full border ${ui.cls} px-3 py-1 text-[10px] uppercase tracking-[0.24em]`}>{ui.label}</span>
              </div>
              {c.label && (<p className="mt-4 font-display text-2xl text-brand-text">{c.label}</p>)}
              {c.contract_id && (<p className="mt-1 font-mono text-xs text-brand-text/40">{c.contract_id}</p>)}
            </div>
            {c.has_pdf && (
              <a
                data-testid="contract-download-button"
                href={`${BACKEND}/api/me/contract/pdf`}
                target="_blank" rel="noreferrer"
                className="rounded border border-brand-gold px-5 py-2.5 text-xs uppercase tracking-[0.24em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
              >Download contract</a>
            )}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-brand-text/70">{ui.body}</p>
          {/* 2026-07-22 — Agreed rate: prominent when set, "Pending review" placeholder when null */}
          <div data-testid="contract-agreed-rate-block" className="mt-6 rounded border border-brand-gold/30 bg-brand-gold/[0.05] px-5 py-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Agreed rate</div>
            {c.agreed_percentage != null ? (
              <div data-testid="contract-agreed-rate-value" className="mt-1 font-display text-3xl text-brand-text">
                {Number(c.agreed_percentage) % 1 === 0
                  ? `${Number(c.agreed_percentage)}%`
                  : `${Number(c.agreed_percentage).toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}%`}
              </div>
            ) : (
              <div data-testid="contract-agreed-rate-pending" className="mt-1 text-sm italic text-brand-text/60">
                Pending review by leadership
              </div>
            )}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 text-[11px] uppercase tracking-[0.18em] text-brand-text/50 sm:grid-cols-4">
            {c.assigned_at && (<div><div>Assigned</div><div className="mt-1 text-brand-text/80">{new Date(c.assigned_at).toLocaleDateString()}</div></div>)}
            {c.signed_at   && (<div><div>Signed</div><div className="mt-1 text-brand-text/80">{new Date(c.signed_at).toLocaleDateString()}</div></div>)}
            {c.active_at   && (<div><div>Active</div><div className="mt-1 text-brand-text/80">{new Date(c.active_at).toLocaleDateString()}</div></div>)}
            {c.expires_at  && (<div><div>Expires</div><div className="mt-1 text-brand-text/80">{new Date(c.expires_at).toLocaleDateString()}</div></div>)}
          </div>
        </div>

        <p className="mt-8 text-xs text-brand-text/40">
          Questions? <a href="mailto:admin@omertabella.com" className="text-brand-gold">admin@omertabella.com</a>
        </p>
      </div>
    </AppShell>
  );
}
